import { Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import type { OffersCarouselCategory } from '@ocx/data-offers';
import type { AltriaOffersNearbyStatus } from '@ocx/feat-local-offers';
import { CategorySelector, LocationSelectorController } from '@ocx/feat-local-offers';
import { OfferCard } from '@ocx/ui-offers';
import { NoDataView, SizingMultiplier } from '@ocx/ui';

import { ExternalBannerCardController } from '../external-banner/external-banner-card.controller';
import { OfferCardController } from '../offer-card/offer-card.controller';
import { OffersCarouselController } from './offers-carousel.controller';
import { messages } from './offers-catalog-body.messages';

import type { ReactNode } from 'react';

export type OffersCatalogBodyProps = {
  isLocalOffersFeatureEnabled: boolean;
  categories: OffersCarouselCategory[];
  selectedCategory: OffersCarouselCategory | null;
  onCategoryChange: (categoryId: string) => void;
  hasAltriaOffersNearby?: AltriaOffersNearbyStatus;
  /** e.g. Home featured strip — rendered after location selector, before category selector */
  featuredSlot?: ReactNode;
  isOffersEmpty?: boolean;
};

export const OffersCatalogBody = ({
  isLocalOffersFeatureEnabled,
  categories,
  selectedCategory,
  onCategoryChange,
  hasAltriaOffersNearby,
  featuredSlot,
  isOffersEmpty,
}: OffersCatalogBodyProps) => {
  return isLocalOffersFeatureEnabled ? (
    <>
      <LocationSelectorController />
      {featuredSlot}
      <CategorySelector
        isSticky
        selectedCategory={selectedCategory}
        onCategoryChange={onCategoryChange}
        categories={categories}
      />
      {isOffersEmpty && (
        <NoDataView title={<FormattedMessage {...messages['offers-catalog-body.no-offers-available-title']} />} />
      )}
      <Stack spacing={SizingMultiplier.sm}>
        {selectedCategory?.banners.map((banner) => (
          <ExternalBannerCardController
            key={`${banner.bannerConfigurationUuid}_${banner.trackingId}`}
            banner={banner}
            hasAltriaOffersNearby={hasAltriaOffersNearby}
          />
        ))}
        {selectedCategory?.offers.map((offer) => (
          <OfferCardController key={offer.id} offer={offer}>
            {(props) => <OfferCard {...props} />}
          </OfferCardController>
        ))}
      </Stack>
    </>
  ) : (
    <>
      {featuredSlot}
      <OffersCarouselController
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={onCategoryChange}
        hasAltriaOffersNearby={hasAltriaOffersNearby}
      />
      {isOffersEmpty && (
        <NoDataView title={<FormattedMessage {...messages['offers-catalog-body.no-offers-available-title']} />} />
      )}
    </>
  );
};
